/* ============================================================
   MYC PERFORMANCE — dual-render scroll stage
   Two hidden <video> elements decode frames; canvases display
   them. Scroll scrubs render A (grip) through the first half of
   the stage, hands over to render B (runner) through the second.
   Renders with a real alpha channel pass straight through; on
   engines that drop VP9 alpha (Safari) each frame is luma-keyed
   in JS (dark px -> transparent) — identical result everywhere,
   no reliance on mix-blend-mode (Safari-broken). Videos are
   fetched as blobs so Safari's byte-range CORS quirks never
   taint the canvas.
   ============================================================ */
(function () {
  'use strict';

  var IS_MOBILE = window.matchMedia && window.matchMedia('(max-width: 959px)').matches;
  var MAX_CANVAS_W = IS_MOBILE ? 360 : 540;

  function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
  function smooth(t) { t = clamp01(t); return t * t * (3 - 2 * t); }

  /* ---------- one render layer: poster, source pick, blob load, luma key ---------- */
  function makeLayer(el) {
    if (!el) return null;
    var video = el.querySelector('video');
    var canvas = el.querySelector('canvas');
    if (!video || !canvas) return null;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });

    var L = {
      el: el,
      video: video,
      canvas: canvas,
      ready: false,
      started: false,
      canKey: true,
      bbox: null, // normalized sock silhouette {x0,y0,x1,y1} — HUD anchors land inside it
      srcWebm: el.dataset.webm || '',
      srcMp4: el.dataset.mp4 || '',
      poster: el.dataset.poster || ''
    };

    /* opaque-pixel bounding box, normalized 0..1. Sampled with a stride so a
       full scan of a 540px frame stays ~50k checks. */
    function scanBBox(d, w, h) {
      var st = 3, minX = w, minY = h, maxX = -1, maxY = -1, x, y, row;
      for (y = 0; y < h; y += st) {
        row = y * w;
        for (x = 0; x < w; x += st) {
          if (d[((row + x) << 2) + 3] > 40) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return null;
      return {
        x0: minX / w,
        y0: minY / h,
        x1: Math.min(1, (maxX + st) / w),
        y1: Math.min(1, (maxY + st) / h)
      };
    }

    function absorbBBox(bb) {
      if (!bb) return;
      var s = L.bbox, k = 0.35;
      if (!s) { L.bbox = bb; return; }
      s.x0 += (bb.x0 - s.x0) * k;
      s.y0 += (bb.y0 - s.y0) * k;
      s.x1 += (bb.x1 - s.x1) * k;
      s.y1 += (bb.y1 - s.y1) * k;
    }

    /* poster first paint (transparent PNG, no keying needed).
       crossOrigin matters: Shopify serves assets from its CDN, and an
       un-CORSed draw would permanently taint the canvas and kill keying. */
    if (L.poster) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        if (canvas.dataset.hasFrame) return;
        var s = Math.min(canvas.width / img.naturalWidth, canvas.height / img.naturalHeight);
        var w = img.naturalWidth * s, h = img.naturalHeight * s;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
        try {
          var id = ctx.getImageData(0, 0, canvas.width, canvas.height);
          absorbBBox(scanBBox(id.data, canvas.width, canvas.height));
        } catch (e) {}
      };
      img.src = L.poster;
    }

    function chooseSrc() {
      if (!L.srcWebm) return L.srcMp4;
      if (!L.srcMp4) return L.srcWebm;
      var canWebm = video.canPlayType('video/webm; codecs="vp9"') || video.canPlayType('video/webm');
      return canWebm ? L.srcWebm : L.srcMp4; // iOS Safari reports '' for webm -> mp4
    }

    L.load = function () {
      if (L.started) return;
      L.started = true;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.addEventListener('loadedmetadata', function () { try { video.currentTime = 0.001; } catch (e) {} });
      video.addEventListener('seeked', L.draw);
      video.addEventListener('loadeddata', function () { L.ready = true; L.draw(); });

      var src = chooseSrc();
      if (!src) return;

      var applyDirect = function () {
        video.addEventListener('error', function () {
          // decode failure (e.g. old Safari + webm) -> try the mp4 render
          if (!video.dataset.fb && L.srcMp4 && src !== L.srcMp4) {
            video.dataset.fb = '1';
            video.src = L.srcMp4;
            video.load();
          }
        });
        video.src = src;
        video.load();
      };

      if (window.fetch) {
        fetch(src)
          .then(function (r) { if (!r.ok) throw 0; return r.blob(); })
          .then(function (b) {
            video.addEventListener('error', function () {
              if (!video.dataset.fb && L.srcMp4 && src !== L.srcMp4) {
                video.dataset.fb = '1';
                fetch(L.srcMp4)
                  .then(function (r) { return r.blob(); })
                  .then(function (b2) { video.src = URL.createObjectURL(b2); video.load(); })
                  .catch(function () { video.src = L.srcMp4; video.load(); });
              }
            });
            video.src = URL.createObjectURL(b);
            video.load();
          })
          .catch(applyDirect);
      } else {
        applyDirect();
      }
    };

    L.draw = function () {
      if (!video.videoWidth || video.readyState < 2) return;
      var scale = Math.min(1, MAX_CANVAS_W / video.videoWidth);
      var w = Math.round(video.videoWidth * scale);
      var h = Math.round(video.videoHeight * scale);
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(video, 0, 0, w, h);
      canvas.dataset.hasFrame = '1';

      if (L.canKey) {
        try {
          var id = ctx.getImageData(0, 0, w, h);
          var d = id.data;
          // skip keying when the render already carries alpha (corners transparent)
          if (d[3] >= 8 || d[d.length - 1] >= 8) {
            for (var i = 0; i < d.length; i += 4) {
              var m = d[i] > d[i + 1] ? d[i] : d[i + 1];
              if (d[i + 2] > m) m = d[i + 2];
              // black -> transparent, ramp to opaque by 56 luma (matches screen-blend look)
              d[i + 3] = m >= 56 ? 255 : (m <= 12 ? 0 : ((m - 12) * 255 / 44) | 0);
            }
            ctx.putImageData(id, 0, 0);
          }
          canvas.style.mixBlendMode = 'normal';
          // track the sock silhouette so HUD callouts pin to the product itself
          absorbBBox(scanBBox(d, w, h));
        } catch (e) {
          // tainted canvas — fall back to CSS blend (works everywhere except old Safari)
          L.canKey = false;
          canvas.style.mixBlendMode = 'screen';
        }
      }
    };

    L.seek = function (p) {
      if (!L.ready) return;
      var dur = video.duration;
      if (dur && isFinite(dur) && video.readyState >= 1 && !video.seeking) {
        var t = p * (dur - 0.06);
        var seekThreshold = IS_MOBILE ? 0.055 : 0.025;
        if (Math.abs(video.currentTime - t) > seekThreshold) { try { video.currentTime = t; } catch (e) {} }
      }
    };

    return L;
  }

  /* ---------- the stage ---------- */
  function initHero(root) {
    if (root.dataset.mycHeroInit) return;
    root.dataset.mycHeroInit = '1';

    var stage = root.querySelector('[data-hero-stage]');
    var visual = root.querySelector('[data-hero-visual]');
    var stack = root.querySelector('[data-hero-stack]');
    var lines = root.querySelector('[data-hero-lines]');
    var arc = root.querySelector('[data-hero-arc]');
    var rotEl = root.querySelector('[data-hero-rot]');
    var frameEl = root.querySelector('[data-hero-frame]');
    var sysEl = root.querySelector('[data-hero-sys]');
    if (!stage) return;

    var layerA = makeLayer(root.querySelector('[data-hero-layer="a"]'));
    var layerB = makeLayer(root.querySelector('[data-hero-layer="b"]'));
    var hasB = !!(layerB && (layerB.srcWebm || layerB.srcMp4));
    if (!hasB) root.classList.add('myc-hero--single');

    var SPLIT = hasB ? 0.5 : 1;
    var FADE = 0.045;

    var labelA = root.dataset.labelA || 'A';
    var labelB = root.dataset.labelB || 'B';

    var cur = 0, target = 0, act = null, lineKey = '', raf = null;
    var heroActive = true;
    var lastPhaseB = false, lastSw = false, lastCtaReady = false, lastRot = '', lastFrame = '', bTimer = null;

    var callouts = Array.prototype.slice.call(root.querySelectorAll('[data-hero-callout]'));
    var switches = Array.prototype.slice.call(root.querySelectorAll('[data-hero-switch]'));

    /* ----- connector lines pinned to the sock silhouette -----
       Anchors are percentages of the SOCK itself (its scanned opaque
       bounding box), measured on the live canvas rect of the callout's
       own phase layer — so dots ride the render through rotation, scale
       and the A/B handover instead of floating in the empty frame. */
    var FALLBACK_BB = { x0: 0.16, y0: 0.05, x1: 0.86, y1: 0.95 };
    function layoutCallouts() {
      if (!visual || !lines || !stack) return;
      var vr = visual.getBoundingClientRect();
      if (!vr.width) return;
      var mob = window.innerWidth < 960;
      var parts = [Math.round(vr.width), Math.round(vr.height)];
      var geo = [];
      callouts.forEach(function (co, i) {
        var icon = co.querySelector('[data-hero-icon]');
        var line = root.querySelector('[data-hero-line="' + i + '"]');
        var dot = root.querySelector('[data-hero-dot="' + i + '"]');
        if (!icon || !line || !dot) return;
        var Lr = (co.dataset.phase === 'b' && layerB) ? layerB : layerA;
        if (!Lr) return;
        var cr = Lr.canvas.getBoundingClientRect(); // includes layer transforms
        if (!cr.width) return;
        var bb = Lr.bbox || FALLBACK_BB;
        var axf = parseFloat(mob ? co.dataset.anchorXMobile : co.dataset.anchorX);
        var ayf = parseFloat(mob ? co.dataset.anchorYMobile : co.dataset.anchorY);
        var ax = cr.left - vr.left + cr.width * (bb.x0 + (bb.x1 - bb.x0) * axf);
        var ay = cr.top - vr.top + cr.height * (bb.y0 + (bb.y1 - bb.y0) * ayf);
        var side = co.dataset.side || 'right';
        var iy, ex;
        if (mob) {
          // mobile: chips hug the screen edges — anchor the line to the card edge
          var kr = co.getBoundingClientRect();
          side = (kr.left + kr.width / 2 - vr.left) > vr.width / 2 ? 'right' : 'left';
          iy = kr.top + kr.height / 2 - vr.top;
          ex = side === 'right' ? (kr.left - vr.left - 8) : (kr.right - vr.left + 8);
        } else {
          var ir = icon.getBoundingClientRect();
          if (!ir.width) return;
          iy = ir.top + ir.height / 2 - vr.top;
          ex = side === 'right' ? (ir.left - vr.left - 10) : (ir.right - vr.left + 10);
        }
        geo.push({ line: line, dot: dot, ax: ax, ay: ay, ex: ex, iy: iy });
        parts.push(Math.round(ax), Math.round(ay), Math.round(ex), Math.round(iy));
      });
      var key = parts.join(',');
      if (key === lineKey) return;
      lineKey = key;
      lines.setAttribute('viewBox', '0 0 ' + Math.round(vr.width) + ' ' + Math.round(vr.height));
      geo.forEach(function (g) {
        // dot -> elbow at card height -> card edge; the elbow sits closer to
        // the card so the diagonal leaves the sock cleanly
        var mx = g.ax + (g.ex - g.ax) * 0.58;
        g.line.setAttribute('points', g.ax.toFixed(1) + ',' + g.ay.toFixed(1) + ' ' + mx.toFixed(1) + ',' + g.iy.toFixed(1) + ' ' + g.ex.toFixed(1) + ',' + g.iy.toFixed(1));
        g.dot.style.left = g.ax.toFixed(1) + 'px';
        g.dot.style.top = g.ay.toFixed(1) + 'px';
      });
    }

    function setActive(idx) {
      if (idx === act) return;
      act = idx;
      callouts.forEach(function (co, i) {
        var on = i === idx;
        co.style.opacity = on ? '1' : '0';
        var line = root.querySelector('[data-hero-line="' + i + '"]');
        var dot = root.querySelector('[data-hero-dot="' + i + '"]');
        if (line) { line.style.transition = 'opacity 0.45s'; line.style.opacity = on ? '0.8' : '0'; }
        if (dot) { dot.style.opacity = on ? '1' : '0'; }
      });
    }

    function pickCallout(phase, prog) {
      var group = [];
      callouts.forEach(function (co, i) {
        if ((co.dataset.phase || 'a') === phase) group.push(i);
      });
      if (!group.length) return -1;
      var n;
      if (group.length === 3) n = prog < 0.34 ? 0 : (prog < 0.7 ? 1 : 2);
      else n = Math.min(group.length - 1, Math.floor(prog * group.length));
      return group[n];
    }

    /* ----- shared update (rAF + scroll fallback both call this) ----- */
    function update(ease) {
      var total = root.offsetHeight - window.innerHeight;
      var top = root.getBoundingClientRect().top;
      var p = total > 0 ? (-top) / total : 0;
      p = clamp01(p);
      target = p;
      if (ease) {
        cur += (target - cur) * 0.14;
        if (Math.abs(target - cur) < 0.0003) cur = target;
      } else {
        cur = target;
      }

      var xf = hasB ? smooth((cur - (SPLIT - FADE)) / (FADE * 2)) : 0;
      var pA = clamp01(SPLIT > 0 ? cur / SPLIT : 1);
      var pB = hasB ? clamp01((cur - SPLIT) / (1 - SPLIT)) : 0;
      var phaseB = hasB && cur >= SPLIT;
      var switching = xf > 0.02 && xf < 0.98;

      stage.style.setProperty('--p', cur.toFixed(4));
      stage.style.setProperty('--xf', xf.toFixed(4));
      stage.style.setProperty('--pb', pB.toFixed(4));

      /* Mobile hero choreography: clear the editorial layer first, let the
         product settle into the full viewport, then reveal the conversion
         tray only for the closing beat of the scrub. CSS owns the motion so
         the scroll listener never captures or cancels a touch gesture. */
      var copyOut = smooth((cur - 0.04) / 0.18);
      var focusIn = smooth((cur - 0.10) / 0.22);
      /* Phase B's final HUD point begins at 85% of the full scrub. Keep a
         deliberate reading beat before the conversion tray replaces it. */
      var ctaReady = cur >= 0.92;
      stage.style.setProperty('--hero-copy-out', copyOut.toFixed(4));
      stage.style.setProperty('--hero-copy-shift', (-74 * copyOut).toFixed(1) + 'px');
      stage.style.setProperty('--hero-focus-x', (6 * (1 - focusIn)).toFixed(2) + 'vw');
      stage.style.setProperty('--hero-focus-y', (11 - 8 * focusIn).toFixed(2) + 'svh');
      stage.style.setProperty('--hero-focus-scale', (1 + focusIn * 0.11).toFixed(4));
      if (ctaReady !== lastCtaReady) {
        lastCtaReady = ctaReady;
        root.classList.toggle('is-cta-ready', ctaReady);
      }

      if (layerA && !layerA.started) layerA.load();
      if (hasB && !layerB.started && cur > 0.08) layerB.load();

      if (layerA && xf < 0.98) layerA.seek(pA);
      if (hasB && layerB && xf > 0.02) layerB.seek(pB);

      if (phaseB !== lastPhaseB) {
        lastPhaseB = phaseB;
        root.classList.toggle('is-phase-b', phaseB);
        switches.forEach(function (btn) {
          btn.setAttribute('aria-pressed', (btn.dataset.heroSwitch === (phaseB ? 'b' : 'a')) ? 'true' : 'false');
        });
        if (sysEl) sysEl.textContent = phaseB ? labelB : labelA;
      }
      if (switching !== lastSw) {
        lastSw = switching;
        root.classList.toggle('is-switching', switching);
      }

      layoutCallouts();
      if (callouts.length) {
        // The opening frame is editorial only. Reveal the first HUD point
        // after the customer has intentionally begun the hero scrub.
        if (switching || cur <= 0.012) setActive(-1);
        else setActive(pickCallout(phaseB ? 'b' : 'a', phaseB ? pB : pA));
      }

      /* HUD telemetry — cheap text updates, only when the value changes */
      var prog = phaseB ? pB : pA;
      if (rotEl) {
        var rot = (Math.round(prog * 360) % 361) + '°';
        if (rot !== lastRot) { lastRot = rot; rotEl.textContent = rot; }
      }
      if (frameEl) {
        var vid = phaseB ? (layerB && layerB.video) : (layerA && layerA.video);
        var fr = 'F' + String(vid && vid.duration ? Math.round(prog * vid.duration * 24) : Math.round(prog * 145)).padStart(3, '0');
        if (fr !== lastFrame) { lastFrame = fr; frameEl.textContent = fr; }
      }
      if (arc) arc.setAttribute('stroke-dashoffset', (100 - prog * 100).toFixed(1));
    }

    function queueUpdate(ease) {
      if (!heroActive || raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        update(!!ease);
      });
    }

    /* ----- HUD product switcher: scroll is the single source of truth ----- */
    switches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var toB = btn.dataset.heroSwitch === 'b';
        var total = root.offsetHeight - window.innerHeight;
        var start = window.scrollY + root.getBoundingClientRect().top;
        var targetP = toB ? (SPLIT + (1 - SPLIT) * 0.42) : SPLIT * 0.42;
        window.scrollTo({ top: Math.round(start + targetP * total), behavior: 'smooth' });
      });
    });

    // Only update while the hero is near the viewport. This avoids canvas
    // reads, video seeks and layout work while customers browse lower sections.
    var onScroll = function () { queueUpdate(false); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('orientationchange', onScroll);
    update(false); // first paint: position geometry; HUD remains hidden at rest

    var heroIO = null;
    if ('IntersectionObserver' in window) {
      heroIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          heroActive = entry.isIntersecting;
          if (heroActive) queueUpdate(false);
        });
      }, { rootMargin: '75% 0px 75% 0px', threshold: 0 });
      heroIO.observe(root);
    }

    // warm render B shortly after load so the handover is instant
    if (hasB) bTimer = setTimeout(function () { if (layerB && !layerB.started) layerB.load(); }, 2200);

    root.__mycHeroDestroy = function () {
      if (raf) cancelAnimationFrame(raf);
      if (bTimer) clearTimeout(bTimer);
      if (heroIO) heroIO.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('orientationchange', onScroll);
    };
  }

  function initAll() {
    document.querySelectorAll('[data-myc-hero]').forEach(initHero);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', function (e) {
    var hero = e.target.querySelector('[data-myc-hero]');
    if (hero) initHero(hero);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var hero = e.target.querySelector('[data-myc-hero]');
    if (hero && hero.__mycHeroDestroy) hero.__mycHeroDestroy();
  });
})();
